import React from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'

import {
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Typography,
  Button,
} from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'

import { Label, SectionCard, TextFieldList } from 'components'
import { ApprovalTable } from './components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'
import schema from './schema'

const ControlledRadioGroup = ({ value, onChange, children }) => {
  const handleChange = (event) => {
    onChange(event.target.value)
  }

  return (
    <RadioGroup value={value || ''} onChange={handleChange}>
      {children}
    </RadioGroup>
  )
}

const ApprovalForm = ({
  documentId,
  handleBackRedirection = () => {},
  setLoading,
  refresh = () => {},
  type = 'edit',
}) => {
  const { AWANTING_APPROVAL_PERSON_STATUS_ID } = constants.documents

  const snackbar = useSnackbar()

  const isShow = type === 'show'

  const { response, isLoading } = useFetch(
    service.dponet.usersCompanies.list,
    { listByCompany: true, perPage: 9999999 },
    [],
  )

  const {
    response: privacyApprovalResponse,
    refresh: refreshPrivacyApprovals,
  } = useFetch(
    service.dponet.privacyPolicies.listPrivacyApprovals,
    { privacyPolicyId: documentId, perPage: 9999999 },
    [documentId],
    !!documentId,
  )

  const privacyApprovals =
    privacyApprovalResponse?.data?.privacyPolicyApprovals || []
  const allUsers = response?.data?.userCompanies?.map((item) => item.user) || []

  const methods = useForm({
    mode: 'onChange',
    validationSchema: schema,
    defaultValues: {
      approvalType: '',
      approvalUser: [],
      approvalEmailInput: '',
      approvalEmailList: [],
    },
  })

  const { control, errors, watch, handleSubmit, setError } = methods
  const approvalType = watch('approvalType')

  const onSubmit = async (data) => {
    if (!data.approvalType) {
      if (privacyApprovals.length === 0) {
        setError('approvalType', 'manual', 'Selecione uma opção de aprovação')
        return
      }
      refresh()
      return
    }

    try {
      setLoading(true)

      const emails =
        data.approvalType === 'email'
          ? data.approvalEmailList
          : data.approvalUser.map((user) => user.email)

      await service.dponet.privacyPolicies.createPrivacyApprovals({
        privacyPolicyId: documentId,
        status: AWANTING_APPROVAL_PERSON_STATUS_ID,
        email: emails,
        approval_type: data.approvalType,
      })

      snackbar.open({
        message: 'Aprovação salva com sucesso!',
        variant: 'success',
      })

      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu uma falha ao salvar a aprovação',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    refreshPrivacyApprovals()
    refresh()
  }

  return (
    <FormContext {...methods}>
      <Box display="flex" flexDirection="column" gridGap={16}>
        <SectionCard title="Aprovação de documento">
          <Box display="flex" flexDirection="column" gridGap={16}>
            <Label title="Aprovação antes da publicação" paddingBoxTitle={0}>
              <Alert variant="standard" severity="info">
                Você pode definir se o documento precisa ser aprovado. Caso a
                aprovação não for definida, ele será considerado automaticamente
                aprovado pelo criador.
              </Alert>
            </Label>
            <Controller
              as={
                <ControlledRadioGroup>
                  <Box mb={1}>
                    <FormControlLabel
                      value="user"
                      disabled={isShow}
                      control={<Radio color="primary" />}
                      label="Selecionar usuário da plataforma"
                    />
                    {approvalType === 'user' && (
                      <>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginLeft: 32, marginBottom: 16 }}
                        >
                          Selecionar um ou mais usuários previamente cadastrados
                          na sua organização
                        </Typography>
                        <Controller
                          as={
                            <Autocomplete
                              multiple
                              filterSelectedOptions
                              disabled={isShow || approvalType !== 'user'}
                              noOptionsText="Nenhum usuário encontrado"
                              options={allUsers}
                              getOptionLabel={(option) => option.name}
                              getOptionSelected={(option, value) =>
                                option.id === value.id
                              }
                              loading={isLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Digite o nome do usuário"
                                  variant="outlined"
                                  error={
                                    approvalType === 'user' &&
                                    !!errors?.approvalUser
                                  }
                                  helperText={
                                    approvalType === 'user'
                                      ? errors?.approvalUser?.message
                                      : ''
                                  }
                                />
                              )}
                            />
                          }
                          control={control}
                          name="approvalUser"
                          onChange={([, data]) => data}
                        />
                      </>
                    )}
                  </Box>

                  <Box>
                    <FormControlLabel
                      value="email"
                      disabled={isShow}
                      control={<Radio color="primary" />}
                      label="Enviar por e-mail"
                    />

                    {approvalType === 'email' && (
                      <>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginLeft: 32, marginBottom: 16 }}
                        >
                          Caso o aprovador ainda não tenha conta, ele poderá
                          acessar o documento por um link seguro, se identificar
                          e aprovar sem precisar se cadastrar -- ou optar por
                          criar uma conta para acompanhar aprovações futuras.
                        </Typography>
                        <TextFieldList
                          label="Adicionar e-mail"
                          controlName="approvalEmailInput"
                          arrayControlName="approvalEmailList"
                          containerTitle="E-mails adicionados"
                          disabled={isShow || approvalType !== 'email'}
                        />
                      </>
                    )}
                  </Box>
                </ControlledRadioGroup>
              }
              control={control}
              name="approvalType"
            />
            {errors?.approvalType && (
              <Typography variant="caption" color="error">
                {errors.approvalType.message}
              </Typography>
            )}
          </Box>
        </SectionCard>

        <SectionCard title="Registro de eventos" isTable>
          <ApprovalTable
            privacyApprovals={privacyApprovals}
            readOnly={isShow}
            onDelete={handleDelete}
          />
        </SectionCard>

        <Box
          display="flex"
          gridGap={theme.spacing(1)}
          justifyContent="flex-end"
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBackRedirection}
          >
            Voltar
          </Button>
          {!isShow && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
            >
              Salvar
            </Button>
          )}
        </Box>
      </Box>
    </FormContext>
  )
}

ApprovalForm.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleBackRedirection: PropTypes.func,
  setLoading: PropTypes.func.isRequired,
  refresh: PropTypes.func,
  type: PropTypes.oneOf(['edit', 'show']),
}

export default ApprovalForm
